import {
  Tabs as KobalteTabs,
  type TabsContentProps,
  type TabsListProps,
  type TabsTriggerProps,
} from "@kobalte/core/tabs";
import { Transition } from "@fleet/ui";
import { createMemo, type JSX, type ParentProps, splitProps } from "solid-js";
import { cn } from "@boveda/shared";

/**
 * Shared, animated Tabs primitive built on Kobalte `Tabs`.
 *
 * Behaviour is kept 100% from Kobalte (focus, keyboard nav, ARIA roles). The
 * only addition is a GPU-only (transform/opacity) transition of the *active
 * panel* when switching tabs, keyed by the active value so the entering panel
 * slides + fades in. Slide direction follows the relative tab index (forward →
 * from the right, backward → from the left); when order can't be inferred it
 * falls back to a neutral fade+rise. `prefers-reduced-motion` is honoured by
 * the global guard in `global.css` (durations collapse to ~0, no movement).
 *
 * Usage:
 *   <Tabs value={tab()} onChange={setTab}>
 *     <TabsList>
 *       <TabsTrigger value="a">A</TabsTrigger>
 *       <TabsTrigger value="b">B</TabsTrigger>
 *     </TabsList>
 *     <TabsPanels value={tab()} order={["a", "b"]}>
 *       <TabsPanel value="a">…</TabsPanel>
 *       <TabsPanel value="b">…</TabsPanel>
 *     </TabsPanels>
 *   </Tabs>
 */

const Tabs = KobalteTabs;

function TabsList(props: TabsListProps) {
  const [local, rest] = splitProps(props as TabsListProps & { class?: string }, ["class"]);
  return (
    <KobalteTabs.List
      class={cn(
        "relative flex items-center gap-1 border-b border-border",
        "data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch data-[orientation=vertical]:border-b-0 data-[orientation=vertical]:border-r",
        local.class,
      )}
      {...rest}
    />
  );
}

function TabsTrigger(props: ParentProps<TabsTriggerProps>) {
  const [local, rest] = splitProps(props as ParentProps<TabsTriggerProps> & { class?: string }, [
    "class",
  ]);
  return (
    <KobalteTabs.Trigger
      class={cn(
        "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium",
        "cursor-pointer text-muted-foreground transition-[color,background-color]",
        "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "data-[selected]:text-foreground disabled:pointer-events-none disabled:opacity-50",
        local.class,
      )}
      {...rest}
    />
  );
}

function TabsIndicator(props: { class?: string }) {
  return (
    <KobalteTabs.Indicator
      class={cn(
        "absolute bg-primary transition-[transform,width,height] duration-200 ease-[var(--ease-elegant,ease)]",
        "bottom-0 h-0.5 data-[orientation=vertical]:bottom-auto data-[orientation=vertical]:right-0 data-[orientation=vertical]:h-auto data-[orientation=vertical]:w-0.5",
        props.class,
      )}
    />
  );
}

/**
 * Animated panels container. Renders only the active panel inside a keyed
 * `<Transition>` so switching tabs cross-fades/slides. Pass `order` (the tab
 * values in display order) to get directional slides; omit it for fade+rise.
 *
 * This intentionally renders a single panel at a time rather than mounting
 * every Kobalte `Tabs.Content`, because animating mount/unmount is what
 * produces the transition. ARIA labelling of the panel is preserved via
 * `TabsPanel` (Kobalte `Tabs.Content`).
 */
interface TabsPanelsProps extends ParentProps {
  /** Currently active tab value (controlled).*/
  value: string;
  /** Tab values in display order; enables directional slide.*/
  order?: readonly string[];
  class?: string;
}

function TabsPanels(props: TabsPanelsProps) {
  // Snapshot of the previously-active value, captured on the first memo run so
  // every `props.*` read stays inside the tracked scope (solid/reactivity).
  let prev: string | undefined;

  const direction = createMemo<"forward" | "backward" | "none">(() => {
    // All reads of `props.*` happen here, inside the memo's tracked scope.
    const from = prev;
    prev = props.value;
    if (from === undefined || !props.order || props.order.length === 0) {
      return "none";
    }
    const fromIdx = props.order.indexOf(from);
    const toIdx = props.order.indexOf(props.value);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) {
      return "none";
    }
    return toIdx > fromIdx ? "forward" : "backward";
  });

  const name = () => {
    const d = direction();
    if (d === "forward") {
      return "tab-panel-forward";
    }
    if (d === "backward") {
      return "tab-panel-backward";
    }
    return "tab-panel-fade";
  };

  return (
    <div class={cn("relative", props.class)}>
      <Transition name={name()} mode="outin">
        {/* Keyed by the active value: changing tab unmounts the old panel and
            mounts the new one, driving the enter/exit transition. */}
        <KeyedPanel key={props.value}>{props.children}</KeyedPanel>
      </Transition>
    </div>
  );
}

/** Remounts whenever `key` changes so the parent <Transition> can animate.*/
function KeyedPanel(props: { key: string; children: JSX.Element }) {
  return (
    <div data-tab-panel={props.key} class="w-full">
      {props.children}
    </div>
  );
}

/**
 * A single panel (Kobalte `Tabs.Content`). `forceMount` is used so the panel
 * inside the keyed wrapper is always rendered while it is the active one; the
 * `TabsPanels` wrapper owns mount/unmount. ARIA (role=tabpanel,
 * aria-labelledby) comes from Kobalte unchanged.
 */
function TabsPanel(props: ParentProps<TabsContentProps & { class?: string }>) {
  const [local, rest] = splitProps(props as ParentProps<TabsContentProps & { class?: string }>, [
    "class",
  ]);
  return (
    <KobalteTabs.Content
      forceMount
      class={cn("outline-none focus-visible:outline-none", local.class)}
      {...rest}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsIndicator, TabsPanels, TabsPanel };
